import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  CheckCircle as CloseIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  SwapHoriz as TransferIcon,
  Payment as PaymentIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import landAcquisitionTransferService from '../../services/landAcquisitionTransferService';
import landAcquisitionPartyService from '../../services/landAcquisitionPartyService';
import { getMozas } from '../../services/landAcquisitionMozaService';
import { addAreas, formatAreaReadable } from '../../utils/landAreaUnits';
import { resolveUploadFileHref } from '../../utils/uploadPaths';
import LandTransferDialog from '../../components/TajResidencia/LandTransferDialog';
import TransferPaymentDialog from '../../components/TajResidencia/TransferPaymentDialog';
import TransferDetailDialog from '../../components/TajResidencia/TransferDetailDialog';

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatCurrency = (n) => new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR', maximumFractionDigits: 0 }).format(n || 0);

export default function LandTransferViewer() {
  const [allRows, setAllRows] = useState([]);
  const [mozas, setMozas] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [mozaFilter, setMozaFilter] = useState('');
  const [dealerFilter, setDealerFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [closingId, setClosingId] = useState(null);
  const [editDialog, setEditDialog] = useState({ open: false, transferId: null, purchaseId: null });
  const [paymentDialog, setPaymentDialog] = useState({ open: false, transferId: null });
  const [detailDialog, setDetailDialog] = useState({ open: false, transferId: null });

  const [searchParams, setSearchParams] = useSearchParams();
  const purchaserParam = searchParams.get('purchaser');
  const missingParam = searchParams.get('missing');

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [searchDebounced, mozaFilter, dealerFilter, purchaserParam, missingParam]);

  useEffect(() => {
    getMozas().then((res) => setMozas(res.data?.data || [])).catch(() => setMozas([]));
    // Load every dealer (active + inactive) so the dropdown is complete
    landAcquisitionPartyService
      .getParties({ type: 'dealer', limit: 100000, page: 1, includeInactive: true })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        const byId = new Map();
        list.forEach((d) => {
          if (!d?._id) return;
          byId.set(String(d._id), d);
        });
        const sorted = Array.from(byId.values()).sort((a, b) =>
          String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
        );
        setDealers(sorted);
      })
      .catch(() => setDealers([]));
  }, []);

  // Ensure any dealer present on loaded transfers also appears in the dropdown
  useEffect(() => {
    if (!allRows.length) return;
    setDealers((prev) => {
      const byId = new Map(prev.map((d) => [String(d._id), d]));
      let changed = false;
      allRows.forEach((row) => {
        const d = row.landPurchase?.dealer;
        if (!d) return;
        const id = String(d._id || d);
        if (!id || byId.has(id)) return;
        byId.set(id, typeof d === 'object' ? d : { _id: id, name: String(d) });
        changed = true;
      });
      if (!changed) return prev;
      return Array.from(byId.values()).sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
      );
    });
  }, [allRows]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Always load the full filtered set, then paginate in the UI so Search / Moza /
      // Dealer / purchaser / missing filters apply across every record — not only the current page.
      const res = await landAcquisitionTransferService.getTransfers({
        page: 1,
        limit: 'all',
        ...(searchDebounced && { search: searchDebounced }),
        ...(mozaFilter && { moza: mozaFilter }),
        ...(dealerFilter && { dealer: dealerFilter }),
        ...(purchaserParam && { purchaser: purchaserParam }),
        ...(missingParam && { missing: missingParam })
      });
      const payload = res.data;
      setAllRows(payload?.transfers || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load land transfers');
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [searchDebounced, mozaFilter, dealerFilter, purchaserParam, missingParam]);

  useEffect(() => {
    load();
  }, [load]);

  const total = allRows.length;
  const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
  const safePage = Math.min(page, maxPage);
  const rows = allRows.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);

  useEffect(() => {
    if (page > maxPage) setPage(maxPage);
  }, [page, maxPage]);

  const totalLandLabel = useMemo(() => {
    if (!allRows.length) return '—';
    return formatAreaReadable(addAreas(...allRows.map((r) => r.transferArea)));
  }, [allRows]);

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete transfer ${row.referenceNo}?`)) return;
    setDeletingId(row._id);
    try {
      await landAcquisitionTransferService.deleteTransfer(row._id);
      toast.success('Land transfer deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCloseTransfer = async (row) => {
    if (!window.confirm(`Close transfer ${row.referenceNo}?`)) return;
    setClosingId(row._id);
    try {
      await landAcquisitionTransferService.closeTransfer(row._id);
      toast.success('Land transfer closed');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to close transfer');
    } finally {
      setClosingId(null);
    }
  };

  const handleExport = async () => {
    const toastId = toast.loading('Preparing export...');
    try {
      // allRows is already the full filtered set (every matching record)
      const allTransfers = allRows;

      if (allTransfers.length === 0) {
        toast.error('No records to export for the current filter', { id: toastId });
        return;
      }

      const xlsx = await import('xlsx');
      const wb = xlsx.utils.book_new();

      const docStatus = (path) => (path && String(path).trim() ? 'Available' : 'Missing');

      const exportData = allTransfers.map((r, idx) => ({
        '#': idx + 1,
        'Date': formatDate(r.transferDate),
        'Reference No': r.referenceNo || '',
        'Seller': r.seller?.name || r.sellerName || '',
        'Purchaser': r.purchaser?.name || r.purchaserName || '',
        'Dealer': r.landPurchase?.dealer?.name || '',
        'Deal No': r.dealNo ?? '',
        'Moza': r.moza?.name || '',
        'Intiqal No': r.intiqalNo || '',
        'Registry No': r.registryNo || '',
        'DOC (INTIQAL)': docStatus(r.inteqalAttachment),
        'DOC (REGISTRY)': docStatus(r.registryAttachment),
        'Area': formatAreaReadable(r.transferArea),
        'Size (Kanals)': r.transferSizeInKanal || 0,
        'Rate/Kanal': r.ratePerKanal || 0,
        'Total Cost': r.totalTransferPayments || 0,
        'Status': r.status || 'Open'
      }));

      const ws = xlsx.utils.json_to_sheet(exportData);
      xlsx.utils.book_append_sheet(wb, ws, 'Land Transfers');

      const filterSuffix =
        missingParam === 'registry'
          ? 'unavailable-registries'
          : missingParam === 'intiqal'
            ? 'unavailable-intiqal'
            : purchaserParam
              ? `purchaser-${String(purchaserParam).replace(/\s+/g, '-').toLowerCase()}`
              : 'all';
      xlsx.writeFile(wb, `land-transfers-${filterSuffix}-${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`Exported ${allTransfers.length} record${allTransfers.length === 1 ? '' : 's'}`, { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Export failed', { id: toastId });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TransferIcon color="primary" sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h4" fontWeight={700}>Manage Transfers</Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage land transfers linked to land purchases.
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export to Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<TransferIcon />}
            onClick={() => setEditDialog({ open: true, transferId: null, purchaseId: null })}
          >
            Create Land Transfer
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="column" spacing={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              size="small"
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Reference, transfer no, purchase, moza..."
              sx={{ minWidth: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
            <TextField
              size="small"
              select
              label="Moza"
              value={mozaFilter}
              onChange={(e) => setMozaFilter(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All mozas</MenuItem>
              {mozas.map((m) => (
                <MenuItem key={m._id} value={m._id}>{m.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              label="Dealer"
              value={dealerFilter}
              onChange={(e) => setDealerFilter(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All dealers</MenuItem>
              {dealers.map((d) => (
                <MenuItem key={d._id} value={d._id}>
                  {d.name || 'Unnamed dealer'}
                  {d.isActive === false ? ' (inactive)' : ''}
                </MenuItem>
              ))}
            </TextField>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                px: 2,
                py: 0.75,
                minWidth: 260,
                flexShrink: 0,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'grey.50'
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Total Land
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
              >
                {loading ? '…' : totalLandLabel}
              </Typography>
            </Box>
          </Stack>
          {purchaserParam && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Filtered by Purchaser:
              </Typography>
              <Chip
                label={purchaserParam}
                onDelete={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('purchaser');
                  setSearchParams(newParams);
                }}
                color="primary"
                size="small"
                variant="outlined"
              />
            </Stack>
          )}
          {missingParam && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Filter:
              </Typography>
              <Chip
                label={missingParam === 'registry' ? 'Un-Available Registries' : 'Un-Available Inteqal'}
                onDelete={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('missing');
                  setSearchParams(newParams);
                }}
                color="warning"
                size="small"
                variant="outlined"
              />
            </Stack>
          )}
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <TableContainer
          sx={{
            maxHeight: 'min(70vh, 720px)',
            overflowX: 'auto',
            overflowY: 'auto'
          }}
        >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>DATE</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>REFERENCE NO</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>SELLER</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>PURCHASER</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>DEALER</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>DEAL NO</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>MOZA</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>INTIQAL</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>REGISTRY</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>DOC (INTIQAL)</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>DOC (REGISTRY)</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>SIZE</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>AMOUNT</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={15} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={15} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                  No land transfers yet — create one from a land purchase record.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => (
                <TableRow key={row._id} hover>
                  <TableCell>{safePage * rowsPerPage + idx + 1}</TableCell>
                  <TableCell>{new Date(row.transferDate).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="text"
                      sx={{ fontFamily: 'monospace', fontWeight: 700, p: 0, minWidth: 0, color: 'text.secondary' }}
                      onClick={() => setEditDialog({ open: true, transferId: row._id, purchaseId: null })}
                    >
                      {row.referenceNo}
                    </Button>
                  </TableCell>
                  <TableCell>{row.seller?.name || row.sellerName || '—'}</TableCell>
                  <TableCell>{row.purchaser?.name || row.purchaserName || '—'}</TableCell>
                  <TableCell>{row.landPurchase?.dealer?.name || '—'}</TableCell>
                  <TableCell>{row.dealNo}</TableCell>
                  <TableCell>{row.moza?.name || '—'}</TableCell>
                  <TableCell>{row.intiqalNo || '—'}</TableCell>
                  <TableCell>{row.registryNo || '—'}</TableCell>
                  <TableCell align="center">
                    {row.inteqalAttachment ? (
                      <Tooltip title="View Inteqal Attachment">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => window.open(resolveUploadFileHref(row.inteqalAttachment), '_blank')}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography component="span" sx={{ color: 'error.main', fontWeight: 800 }}>✖</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {row.registryAttachment ? (
                      <Tooltip title="View Registry Attachment">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => window.open(resolveUploadFileHref(row.registryAttachment), '_blank')}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography component="span" sx={{ color: 'error.main', fontWeight: 800 }}>✖</Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatAreaReadable(row.transferArea)}</TableCell>
                  <TableCell align="right">{formatCurrency(row.totalTransferPayments)}</TableCell>
                  <TableCell align="right">
                    {row.status !== 'Closed' && (
                      <Tooltip title="Close transfer">
                        <IconButton
                          size="small"
                          color="success"
                          disabled={closingId === row._id}
                          onClick={() => handleCloseTransfer(row)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {row.status !== 'Closed' && (
                      <Tooltip title="Pay Transfer">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => setPaymentDialog({ open: true, transferId: row._id })}
                        >
                          <PaymentIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => setDetailDialog({ open: true, transferId: row._id })}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => setEditDialog({ open: true, transferId: row._id, purchaseId: null })}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        disabled={deletingId === row._id}
                        onClick={() => handleDelete(row)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={safePage}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <LandTransferDialog
        open={editDialog.open}
        transferId={editDialog.transferId}
        purchaseId={editDialog.purchaseId}
        onClose={() => setEditDialog({ open: false, transferId: null, purchaseId: null })}
        onSaved={load}
      />

      <TransferPaymentDialog
        open={paymentDialog.open}
        transferId={paymentDialog.transferId}
        onClose={() => setPaymentDialog({ open: false, transferId: null })}
        onSaved={load}
      />
      <TransferDetailDialog
        open={detailDialog.open}
        transferId={detailDialog.transferId}
        onClose={() => setDetailDialog({ open: false, transferId: null })}
      />
    </Box>
  );
}
